import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableContainer,
  TableRow,
  TableBody,
  TableCell,
  Paper,
  Card,
  CardHeader,
  CardContent,
  alpha,
  useTheme,
} from "@mui/material";
import { calculateTotalsByIncomeTypeAndCountry } from "./calculations";
import { formatAmount } from "../../../utils/hooks/dataHandler";
import EmptySummary from "../CA/CaSchedules/utils/EmptySummary";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { TextFieldForViewOnly } from "../../utils/TextField/TextField";

export const outputMapping = {
  Rental: "Other sourced income",
  Interest: "Other sourced income",
  Dividend: "Other sourced income",
  Royalty: "Other sourced income",
  "Trade / Branch Profits": "Tax adjustment",
};

const Summary = ({
  data,
  ftcClaims = {},
  onFtcClaimChange,
  canEdit = true,
}) => {
  const theme = useTheme();

  // Memoize filtered data and calculations to only trigger when data changes
  const { filteredData, totals } = useMemo(() => {
    // Filter out rows where income received in Singapore is zero
    const filtered = data.filter((row) => (row.incomeReceived || 0) > 0);
    const calculatedTotals = calculateTotalsByIncomeTypeAndCountry(filtered);
    return { filteredData: filtered, totals: calculatedTotals };
  }, [data]);

  // Grand totals memoized to recompute only when totals object changes
  const grandTotals = useMemo(() => {
    const agg = {
      incomeReceived: 0,
      shareOfExpensesRemitted: 0,
      netIncomeReceived: 0,
      shareOfForeignTaxSufferedRemitted: 0,
    };

    Object.keys(totals).forEach((incomeType) => {
      Object.keys(totals[incomeType]).forEach((country) => {
        const countryTotals = totals[incomeType][country];
        agg.incomeReceived += countryTotals.incomeReceived || 0;
        agg.shareOfExpensesRemitted +=
          countryTotals.shareOfExpensesRemitted || 0;
        agg.netIncomeReceived += countryTotals.netIncomeReceived || 0;
        agg.shareOfForeignTaxSufferedRemitted +=
          countryTotals.shareOfForeignTaxSufferedRemitted || 0;
      });
    });

    return agg;
  }, [totals]);

  return (
    <Card
      variant='outlined'
      sx={{
        mt: 3,
        borderRadius: 2,
        borderColor: "divider",
        boxShadow: "none",
      }}
    >
      <CardHeader
        title={
          <Typography variant='subtitle1' fontWeight={700}>
            Foreign Income Tracker Summary
          </Typography>
        }
      />
      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        {Object.keys(totals).length === 0 ? (
          <Box sx={{ p: 3 }}>
            <EmptySummary caType='Foreign Income Tracker' />
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ backgroundColor: "background.paper" }}
          >
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.4),
                    }}
                  >
                    Income Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.4),
                    }}
                  >
                    Country
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.4),
                    }}
                  >
                    Income Received <br />
                    (Remitted)
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.4),
                    }}
                  >
                    Share of Expenses <br />
                    (Remitted)
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.4),
                    }}
                  >
                    Net Remitted Income
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.4),
                    }}
                  >
                    Foreign Tax Allocated <br />
                    to Remitted Income
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      textTransform: "uppercase",
                      letterSpacing: "0.06em",
                      borderBottom: "1px solid",
                      borderColor: alpha(theme.palette.divider, 0.4),
                    }}
                  >
                    FTC Claim
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(totals).flatMap((incomeType) => {
                  const countries = totals[incomeType];
                  return Object.keys(countries).map((country) => {
                    const countryTotals = countries[country];
                    const incomeTypeKey = incomeType || "";
                    const countryKey = country || "";
                    const isFtcClaimed =
                      ftcClaims[incomeTypeKey]?.[countryKey] || false;
                    const canClaimFtc =
                      (countryTotals.netIncomeReceived || 0) > 0 &&
                      (countryTotals.shareOfForeignTaxSufferedRemitted || 0) >
                        0;

                    return (
                      <TableRow
                        key={`${incomeType}-${country}`}
                        sx={{
                          "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.03),
                          },
                          transition: "background-color 0.2s ease-in-out",
                        }}
                      >
                        <TableCell
                          sx={{
                            fontSize: "0.8rem",
                            borderBottom: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.2),
                            p: 1.25,
                          }}
                        >
                          {incomeType || "Unspecified"}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.8rem",
                            borderBottom: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.2),
                            p: 1.25,
                          }}
                        >
                          {country || "Unspecified"}
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{
                            fontSize: "0.8rem",
                            borderBottom: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.2),
                            p: 1.25,
                            fontWeight: 600,
                          }}
                        >
                          {formatAmount(countryTotals.incomeReceived || 0)}
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{
                            fontSize: "0.8rem",
                            borderBottom: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.2),
                            p: 1.25,
                            fontWeight: 600,
                          }}
                        >
                          {formatAmount(
                            countryTotals.shareOfExpensesRemitted || 0
                          )}
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{
                            fontSize: "0.8rem",
                            borderBottom: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.2),
                            p: 1.25,
                            fontWeight: 600,
                          }}
                        >
                          {formatAmount(countryTotals.netIncomeReceived || 0)}
                        </TableCell>
                        <TableCell
                          align='right'
                          sx={{
                            fontSize: "0.8rem",
                            borderBottom: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.2),
                            p: 1.25,
                            fontWeight: 600,
                          }}
                        >
                          {formatAmount(
                            countryTotals.shareOfForeignTaxSufferedRemitted ||
                              0,
                            false
                          )}
                        </TableCell>
                        <TableCell
                          align='center'
                          sx={{
                            fontSize: "0.8rem",
                            borderBottom: "1px solid",
                            borderColor: alpha(theme.palette.divider, 0.2),
                            p: 1.25,
                          }}
                        >
                          {canEdit && onFtcClaimChange && (
                            <IconButtonWithTooltip
                              icon={
                                isFtcClaimed
                                  ? AssignmentTurnedInIcon
                                  : RequestQuoteIcon
                              }
                              onClick={() =>
                                onFtcClaimChange(
                                  incomeTypeKey,
                                  countryKey,
                                  !isFtcClaimed
                                )
                              }
                              tooltipTitle={
                                !canClaimFtc
                                  ? "Cannot claim FTC: Net Remitted Income is 0 or no Foreign Tax Allocated to Remitted Income"
                                  : isFtcClaimed
                                  ? "Foreign Tax Credit Claimed"
                                  : "Claim as Foreign Tax Credit"
                              }
                              color={isFtcClaimed ? "success" : "warning"}
                              size='small'
                              disabled={!canClaimFtc}
                            />
                          )}
                          {!canEdit && onFtcClaimChange && (
                            <TextFieldForViewOnly
                              value={isFtcClaimed ? "Yes" : "No"}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  });
                })}
                <TableRow
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.04),
                    borderTop: "2px solid",
                    borderColor: alpha(theme.palette.divider, 0.3),
                  }}
                >
                  <TableCell
                    colSpan={2}
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      p: 1.25,
                    }}
                  >
                    <strong>Grand Total</strong>
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontSize: "0.8rem",
                      p: 1.25,
                      fontWeight: 600,
                    }}
                  >
                    <strong>{formatAmount(grandTotals.incomeReceived)}</strong>
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontSize: "0.8rem",
                      p: 1.25,
                      fontWeight: 600,
                    }}
                  >
                    <strong>
                      {formatAmount(grandTotals.shareOfExpensesRemitted)}
                    </strong>
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontSize: "0.8rem",
                      p: 1.25,
                      fontWeight: 600,
                    }}
                  >
                    <strong>
                      {formatAmount(grandTotals.netIncomeReceived)}
                    </strong>
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontSize: "0.8rem",
                      p: 1.25,
                      fontWeight: 600,
                    }}
                  >
                    <strong>
                      {formatAmount(
                        grandTotals.shareOfForeignTaxSufferedRemitted,
                        false
                      )}
                    </strong>
                  </TableCell>
                  <TableCell sx={{ fontSize: "0.8rem", p: 1.25 }}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default Summary;
